import React, { useEffect, useState, useRef } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaTrash, FaArrowLeft, FaArrowRight, FaStar } from 'react-icons/fa';
import { obtenerProductoPorId, actualizarProducto, crearProducto, obtenerProductos } from '../../services/productoService';
import { subirImagen, eliminarImagen } from '../../services/imagenService';
import { obtenerCategorias } from '../../services/categoriaService';
import { ROUTES, CATEGORIAS, labelCajaTipo, DESTACADOS_MAX } from '../../constants';
import { toNumber, parseMedidasFromModelo } from '../../utils/formatters';
import { obtenerURLImagen, obtenerImagenesProducto } from '../../utils/imageUtils';
import './Admin.css';

let galleryKeySeq = 0;
const nextGalleryKey = () => `img-${Date.now()}-${galleryKeySeq++}`;

function buildExistingItems(producto) {
  return obtenerImagenesProducto(producto).map((src) => ({
    key: nextGalleryKey(),
    kind: 'existing',
    src,
    preview: obtenerURLImagen(src),
  }));
}

function AdminEditarProducto() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const initialImagenRef = useRef({ imagenes: [], imagenGestionadaPorApi: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [removedExisting, setRemovedExisting] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [destacadosCount, setDestacadosCount] = useState(0);
  const [loadedAsDestacado, setLoadedAsDestacado] = useState(false);

  const [formData, setFormData] = useState({
    modelo: '',
    material: '',
    color: '',
    cataccesorios: '',
    descripcion: '',
    precio: '',
    stock: '',
    categoria: CATEGORIAS.FUNDAS,
    destacado: false,
  });

  const esNuevo = location.pathname.includes('/nuevo') || !id;
  const esCaja = formData.categoria === CATEGORIAS.FUNDAS;
  const otrosDestacados = destacadosCount - (loadedAsDestacado ? 1 : 0);
  const puedeMarcarDestacado = formData.destacado || otrosDestacados < DESTACADOS_MAX;

  useEffect(() => {
    return () => {
      galleryItems.forEach((item) => {
        if (item.kind === 'pending' && item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCategorias = async () => {
      setLoadingCategorias(true);
      try {
        const list = await obtenerCategorias();
        if (!cancelled) setCategorias(list);
      } catch (err) {
        console.warn('No se pudieron cargar categorías:', err);
        if (!cancelled) setCategorias([]);
      } finally {
        if (!cancelled) setLoadingCategorias(false);
      }
    };
    loadCategorias();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDestacados = async () => {
      try {
        const list = await obtenerProductos({ destacado: true });
        if (!cancelled) setDestacadosCount(Array.isArray(list) ? list.length : 0);
      } catch (err) {
        console.warn('No se pudo contar destacados:', err);
      }
    };
    loadDestacados();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (esNuevo) {
      initialImagenRef.current = { imagenes: [], imagenGestionadaPorApi: false };
      setGalleryItems([]);
      setRemovedExisting([]);
      setLoadedAsDestacado(false);
      setLoading(false);
      return;
    }

    const fetchProducto = async () => {
      if (!id) {
        setError('ID de producto no válido');
        setLoading(false);
        return;
      }

      try {
        const producto = await obtenerProductoPorId(id);
        if (!producto) {
          setError('Producto no encontrado');
          setLoading(false);
          return;
        }

        const gestionada = producto.imagenGestionadaPorApi === true;
        initialImagenRef.current = {
          imagenes: obtenerImagenesProducto(producto),
          imagenGestionadaPorApi: gestionada,
        };
        setGalleryItems(buildExistingItems(producto));
        setRemovedExisting([]);
        setFormData({
          modelo: producto.modelo || '',
          material: producto.material || '',
          color: producto.color || '',
          cataccesorios: producto.cataccesorios || '',
          descripcion: producto.descripcion || '',
          precio: producto.precio != null ? String(producto.precio) : '',
          stock: producto.stock != null ? String(producto.stock) : '',
          categoria: producto.categoria || CATEGORIAS.FUNDAS,
          destacado: producto.destacado === true,
        });
        setLoadedAsDestacado(producto.destacado === true);
      } catch (e) {
        console.error('Error al cargar producto:', e);
        setError(e.message || 'Error al cargar el producto');
      } finally {
        setLoading(false);
      }
    };

    fetchProducto();
  }, [id, esNuevo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'destacado' && checked) {
      const otrosDestacados = destacadosCount - (loadedAsDestacado ? 1 : 0);
      if (otrosDestacados >= DESTACADOS_MAX) {
        setError(
          `Ya hay ${DESTACADOS_MAX} productos destacados. Desmarcá uno para marcar otro.`
        );
        return;
      }
    }
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      if (name === 'categoria' && value === CATEGORIAS.FUNDAS) {
        next.stock = '';
        next.color = '';
        next.cataccesorios = '';
      }
      return next;
    });
  };

  const handleArchivosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/webp'];
    const invalid = files.find((f) => !allowedTypes.includes(f.type));
    if (invalid) {
      setError('Solo se permiten imágenes PNG, JPEG o WebP');
      e.target.value = '';
      return;
    }
    const tooBig = files.find((f) => f.size > 5 * 1024 * 1024);
    if (tooBig) {
      setError('Cada imagen no puede superar los 5MB');
      e.target.value = '';
      return;
    }

    setError(null);
    setGalleryItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        key: nextGalleryKey(),
        kind: 'pending',
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
    e.target.value = '';
  };

  const moveItem = (index, direction) => {
    setGalleryItems((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const setAsCover = (index) => {
    if (index <= 0) return;
    setGalleryItems((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.unshift(item);
      return next;
    });
  };

  const removeItem = (index) => {
    setGalleryItems((prev) => {
      const item = prev[index];
      if (!item) return prev;
      if (item.kind === 'pending' && item.preview) {
        URL.revokeObjectURL(item.preview);
      }
      if (item.kind === 'existing' && item.src) {
        setRemovedExisting((removed) =>
          removed.includes(item.src) ? removed : [...removed, item.src]
        );
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const rollbackUploads = async (nombres) => {
    for (const n of nombres) {
      try {
        await eliminarImagen(n);
      } catch (_) {
        /* ignore */
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    const nombresSubidosEstaSesion = [];

    try {
      const imagenesFinales = [];
      for (const item of galleryItems) {
        if (item.kind === 'existing') {
          imagenesFinales.push(item.src);
          continue;
        }
        const nombre = await subirImagen(item.file, null);
        nombresSubidosEstaSesion.push(nombre);
        imagenesFinales.push(nombre);
      }

      const habiaGestionadas = initialImagenRef.current.imagenGestionadaPorApi;
      const hayNuevas = nombresSubidosEstaSesion.length > 0;
      const imagenGestionadaFlag = hayNuevas || (habiaGestionadas && imagenesFinales.length > 0);

      const precioNum = formData.precio ? toNumber(formData.precio) : null;
      const medida = formData.modelo.trim();
      const parsed = esCaja ? parseMedidasFromModelo(medida) : null;

      const catSeleccionada = categorias.find(
        (c) => c.slug === formData.material || c.nombre === formData.material
      );
      const materialValor = catSeleccionada
        ? labelCajaTipo(catSeleccionada.nombre)
        : formData.material.trim()
          ? labelCajaTipo(formData.material)
          : null;

      const updateData = {
        modelo: medida || null,
        material: esCaja ? materialValor : formData.material.trim() || null,
        color: esCaja ? null : formData.color.trim() || null,
        cataccesorios: esCaja ? null : formData.cataccesorios.trim() || null,
        descripcion: formData.descripcion.trim() || null,
        precio: precioNum,
        preciomenor: precioNum,
        stock: esCaja ? null : formData.stock ? toNumber(formData.stock) : null,
        categoria: formData.categoria.trim() || CATEGORIAS.FUNDAS,
        ancho: parsed?.ancho ?? null,
        alto: parsed?.alto ?? null,
        profundidad: parsed?.profundidad ?? null,
        imagenes: imagenesFinales,
        imagenURL: imagenesFinales[0] || null,
        imagenGestionadaPorApi: imagenGestionadaFlag,
        destacado: formData.destacado,
      };

      if (esCaja && !updateData.material) {
        setError('Elegí una categoría.');
        setSaving(false);
        await rollbackUploads(nombresSubidosEstaSesion);
        return;
      }
      if (!updateData.precio || updateData.precio <= 0) {
        setError('Ingresá un precio válido.');
        setSaving(false);
        await rollbackUploads(nombresSubidosEstaSesion);
        return;
      }
      if (!updateData.modelo) {
        setError('Ingresá la medida.');
        setSaving(false);
        await rollbackUploads(nombresSubidosEstaSesion);
        return;
      }

      try {
        if (esNuevo) {
          await crearProducto(updateData);
        } else {
          await actualizarProducto(id, updateData);
        }
      } catch (fsErr) {
        console.error('Error al guardar producto:', fsErr);
        await rollbackUploads(nombresSubidosEstaSesion);
        setError(
          fsErr.message ||
            'Las imágenes se subieron pero no se pudo guardar el producto. Intentá de nuevo.'
        );
        setSaving(false);
        return;
      }

      if (imagenGestionadaFlag || habiaGestionadas) {
        for (const src of removedExisting) {
          if (!src || String(src).includes('http') || String(src).includes('firebasestorage')) {
            continue;
          }
          try {
            await eliminarImagen(src);
          } catch (delErr) {
            console.warn('No se pudo eliminar imagen removida:', delErr);
          }
        }
      }

      galleryItems.forEach((item) => {
        if (item.kind === 'pending' && item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });

      setSuccess(true);
      setSaving(false);
      setTimeout(() => {
        navigate(ROUTES.ADMIN_PRODUCTOS);
      }, 1200);
    } catch (e) {
      console.error('Error al procesar producto:', e);
      await rollbackUploads(nombresSubidosEstaSesion);
      setError(
        e.message ||
          (esNuevo
            ? 'Error al crear el producto. Por favor, intenta nuevamente.'
            : 'Error al actualizar el producto. Por favor, intenta nuevamente.')
      );
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  const hayPendientes = galleryItems.some((i) => i.kind === 'pending');
  const tituloPreview =
    [formData.material ? labelCajaTipo(formData.material) : '', formData.modelo]
      .filter(Boolean)
      .join(' ') || '—';

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="admin-section-title mb-0">
          {esNuevo ? 'Nuevo Producto' : 'Editar Producto'}
        </h5>
        <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}>
          Volver al listado
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mt-3">
          {esNuevo
            ? 'Producto creado exitosamente. Redirigiendo...'
            : 'Producto actualizado exitosamente. Redirigiendo...'}
        </Alert>
      )}

      <div className="admin-form-container">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Categoría</Form.Label>
                <Form.Select
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  className="admin-form-select"
                  required={esCaja}
                  disabled={loadingCategorias}
                >
                  <option value="">
                    {loadingCategorias ? 'Cargando…' : 'Seleccionar categoría'}
                  </option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nombre}>
                      {labelCajaTipo(cat.nombre)}
                    </option>
                  ))}
                  {formData.material &&
                    !categorias.some(
                      (c) => c.nombre === formData.material || c.slug === formData.material
                    ) && (
                      <option value={formData.material}>
                        {labelCajaTipo(formData.material)} (legado)
                      </option>
                    )}
                </Form.Select>
                <Form.Text className="text-muted">
                  Administrá categorías desde el menú lateral → Categorías.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Medida</Form.Label>
                <Form.Control
                  type="text"
                  name="modelo"
                  value={formData.modelo}
                  onChange={handleChange}
                  placeholder="Ej: 60x40x40"
                  className="admin-form-control"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Título (vista previa)</Form.Label>
                <Form.Control
                  type="text"
                  value={tituloPreview}
                  className="admin-form-control"
                  disabled
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Texto que se muestra al abrir el producto"
                  className="admin-form-control"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Precio</Form.Label>
                <Form.Control
                  type="number"
                  name="precio"
                  value={formData.precio}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.01"
                  min="0"
                  className="admin-form-control"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12}>
              <Form.Group className="admin-form-group">
                <Form.Label className="admin-form-label">Imágenes del producto</Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  onChange={handleArchivosChange}
                  className="admin-form-control"
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Podés subir varias. La primera es la portada. PNG, JPEG o WebP (máx. 5MB c/u).
                </Form.Text>
              </Form.Group>

              {galleryItems.length > 0 && (
                <ul className="admin-gallery-list">
                  {galleryItems.map((item, index) => (
                    <li key={item.key} className="admin-gallery-item">
                      <div className="admin-gallery-thumb-wrap">
                        <img
                          src={item.preview}
                          alt={`Imagen ${index + 1}`}
                          className="admin-gallery-thumb"
                        />
                        {index === 0 && (
                          <span className="admin-gallery-cover-badge">Portada</span>
                        )}
                      </div>
                      <div className="admin-gallery-actions">
                        <button
                          type="button"
                          className="admin-gallery-btn"
                          onClick={() => moveItem(index, -1)}
                          disabled={saving || index === 0}
                          aria-label="Mover a la izquierda"
                          title="Mover a la izquierda"
                        >
                          <FaArrowLeft />
                        </button>
                        <button
                          type="button"
                          className="admin-gallery-btn"
                          onClick={() => moveItem(index, 1)}
                          disabled={saving || index === galleryItems.length - 1}
                          aria-label="Mover a la derecha"
                          title="Mover a la derecha"
                        >
                          <FaArrowRight />
                        </button>
                        <button
                          type="button"
                          className="admin-gallery-btn"
                          onClick={() => setAsCover(index)}
                          disabled={saving || index === 0}
                          aria-label="Usar como portada"
                          title="Usar como portada"
                        >
                          <FaStar />
                        </button>
                        <button
                          type="button"
                          className="admin-gallery-btn admin-gallery-btn--danger"
                          onClick={() => removeItem(index)}
                          disabled={saving}
                          aria-label="Quitar imagen"
                          title="Quitar imagen"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12}>
              <div
                className="border rounded p-3 admin-checkbox-container"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <Form.Group className="mb-0 d-flex align-items-center justify-content-between w-100">
                  <Form.Check
                    type="checkbox"
                    name="destacado"
                    label={<strong style={{ fontSize: '1.1rem' }}>Producto destacado</strong>}
                    checked={formData.destacado}
                    onChange={handleChange}
                    disabled={!puedeMarcarDestacado}
                    style={{ fontSize: '1.2rem' }}
                    className="admin-checkbox-destacado mb-0"
                  />
                  <Form.Text className="text-muted mb-0 ms-3">
                    {puedeMarcarDestacado
                      ? `Aparece en el inicio · ${otrosDestacados + (formData.destacado ? 1 : 0)} / ${DESTACADOS_MAX}`
                      : `Máximo ${DESTACADOS_MAX} destacados. Desmarcá otro para marcar este.`}
                  </Form.Text>
                </Form.Group>
              </div>
            </Col>
          </Row>

          <div className="admin-form-actions d-flex gap-2">
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {hayPendientes
                    ? 'Subiendo imágenes y guardando...'
                    : esNuevo
                      ? 'Creando producto...'
                      : 'Guardando...'}
                </>
              ) : esNuevo ? (
                'Crear Producto'
              ) : (
                'Guardar Cambios'
              )}
            </Button>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => navigate(ROUTES.ADMIN_PRODUCTOS)}
              disabled={saving}
            >
              Cancelar
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
}

export default AdminEditarProducto;
